import type { InvoiceLineItem, EntityMatch } from '@acitra/shared';
import { amountsMatch } from '@acitra/shared';
import { EditableValue } from '../../../components/EditableValue';
import { ValidationIndicator } from './ValidationIndicator';

interface TotalsSectionProps {
  lineItems: InvoiceLineItem[];
  subtotal?: number;
  editedSubtotal?: number | null;
  taxes?: { name: string; amount: number }[];
  total?: number;
  editedTotal?: number | null;
  currency?: string;
  currencyMatch?: EntityMatch;
  formatAmount: (amount: number | undefined) => string;
  onSubtotalEdit: (value: string | null) => Promise<void>;
  onTotalEdit: (value: string | null) => Promise<void>;
  onSubtotalReset?: () => Promise<void>;
  onTotalReset?: () => Promise<void>;
}

function calculateSubtotal(lineItems: { amount?: number }[]): number {
  const sum = lineItems.reduce((acc, item) => acc + (item.amount || 0), 0);
  return Math.round(sum * 100) / 100;
}

function calculateTotal(
  subtotal: number | undefined,
  taxes: { amount: number }[] | undefined
): number {
  const taxSum = taxes?.reduce((sum, t) => sum + t.amount, 0) || 0;
  return Math.round(((subtotal || 0) + taxSum) * 100) / 100;
}

export function TotalsSection({
  lineItems,
  subtotal,
  editedSubtotal,
  taxes,
  total,
  editedTotal,
  currency,
  currencyMatch,
  formatAmount,
  onSubtotalEdit,
  onTotalEdit,
  onSubtotalReset,
  onTotalReset,
}: TotalsSectionProps) {
  const calculatedSubtotal = calculateSubtotal(lineItems);

  // Get effective subtotal (edits > extracted > calculated)
  const effectiveSubtotal =
    editedSubtotal !== undefined
      ? (editedSubtotal ?? calculatedSubtotal)
      : (subtotal ?? calculatedSubtotal);

  const isSubtotalCalculated = editedSubtotal === undefined && subtotal === undefined;

  // Use effective subtotal for total validation
  const calculatedTotal = calculateTotal(effectiveSubtotal, taxes);

  // Get effective total (edits > extracted)
  const effectiveTotal = editedTotal !== undefined ? (editedTotal ?? total) : total;

  // For validation indicator: null = can't validate, true = match, false = mismatch
  const subtotalValid = isSubtotalCalculated
    ? true // Calculated subtotal is always "valid" (it's the source of truth)
    : effectiveSubtotal == null
      ? null
      : amountsMatch(effectiveSubtotal, calculatedSubtotal);

  const totalValid = effectiveTotal == null ? null : amountsMatch(effectiveTotal, calculatedTotal);

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="py-4">
        <div className="flex justify-between items-center py-2">
          <div className="pl-6">
            <span className="text-sm text-gray-500">Subtotal</span>
            {isSubtotalCalculated && (
              <span className="ml-2 text-xs text-amber-600" title="Calculated from line items">
                (calculated)
              </span>
            )}
          </div>
          <div className="flex items-center pr-2">
            <EditableValue
              value={formatAmount(effectiveSubtotal)}
              onSave={onSubtotalEdit}
              inputMode="decimal"
              textAlign="right"
              width="w-28"
              className={typeof editedSubtotal === 'number' ? '!text-edited-600' : ''}
              originalValue={
                // If there's an edit, show original (extracted or calculated) for reset
                editedSubtotal !== undefined
                  ? formatAmount(subtotal ?? calculatedSubtotal)
                  : undefined
              }
              onReset={onSubtotalReset}
              calculatedValue={calculatedSubtotal}
              onCalculate={async () => {
                await onSubtotalEdit(String(calculatedSubtotal));
              }}
            />
            <div className="w-8 px-2 flex justify-center">
              {!isSubtotalCalculated && (
                <ValidationIndicator
                  isValid={subtotalValid}
                  captured={effectiveSubtotal}
                  calculated={calculatedSubtotal}
                  formatAmount={formatAmount}
                />
              )}
            </div>
          </div>
        </div>
        {taxes && taxes.length > 0 && (
          <>
            {taxes.map((tax, index) => {
              // Calculate tax percentage, rounded to nearest 0.25%
              const taxPercent =
                effectiveSubtotal > 0
                  ? Math.round((tax.amount / effectiveSubtotal) * 400) / 4
                  : null;
              return (
                <div key={index} className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-500 pl-6">{tax.name}</span>
                  <div className="flex items-center pr-2">
                    {taxPercent !== null && (
                      <span className="text-sm text-gray-400 mr-2">({taxPercent}%)</span>
                    )}
                    <span className="text-sm font-medium text-gray-900 text-right w-28 py-1.5 px-2">
                      {formatAmount(tax.amount)}
                    </span>
                    <div className="w-8 px-2" />
                  </div>
                </div>
              );
            })}
          </>
        )}
        <div className="flex justify-between items-center py-2 mt-2 border-t border-gray-200">
          <div className="pl-6">
            <span className="text-base font-medium text-gray-900">Total</span>
          </div>
          <div className="flex items-center pr-2">
            {(currencyMatch || currency) && (
              <span className="text-base font-semibold text-gray-900 mr-2">
                {currencyMatch?.entityName || currency}
              </span>
            )}
            <EditableValue
              value={formatAmount(effectiveTotal)}
              onSave={onTotalEdit}
              inputMode="decimal"
              textAlign="right"
              width="w-28"
              className={`text-base font-semibold ${
                typeof editedTotal === 'number' ? '!text-edited-600' : ''
              }`}
              originalValue={
                // If there's an edit, show original (extracted or calculated) for reset
                editedTotal !== undefined ? formatAmount(total ?? calculatedTotal) : undefined
              }
              onReset={onTotalReset}
              calculatedValue={calculatedTotal}
              onCalculate={async () => {
                await onTotalEdit(String(calculatedTotal));
              }}
            />
            <div className="w-8 px-2 flex justify-center">
              <ValidationIndicator
                isValid={totalValid}
                captured={effectiveTotal}
                calculated={calculatedTotal}
                formatAmount={formatAmount}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
